import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';
import { Switch } from 'sharedComponents';
import { IconAI, IconInfo } from 'svgIcons/MotionPortalIcons';
import DiagnosisCard from './DiagnosisCard';
import './smartConditionSummary.scss';

const SmartConditionSummary = ({ smartConditionSummaryData }) => {
    const { t: translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const hasValidDiagnosisData = smartConditionSummaryData &&
        Array.isArray(smartConditionSummaryData) &&
        smartConditionSummaryData?.length > 0;

    return (
        <div className='smart-condition-summary' id='smart-condition-summary'>
            {/* Header Section */}
            <div className='smart-condition-summary__header' id='smart-condition-summary-header'>
                <div className='smart-condition-summary__header-content' id='smart-condition-summary-header-content'>
                    <div className='smart-condition-summary__title-row' id='smart-condition-summary-title-row'>
                        <IconAI height={24} width={24} />
                        <span className='smart-condition-summary__title' id='smart-condition-summary-title'>
                            {translate('ABB.Powertrain.Frontend.assetSmartConditionSummaryTitle')}
                        </span>
                    </div>
                    <p className='smart-condition-summary__description' id='smart-condition-summary-description'>
                        {translate('ABB.Powertrain.Frontend.assetSmartConditionSummaryDescription')}
                    </p>
                </div>
                <div className='smart-condition-summary__ai-feature' id='smart-condition-summary-ai-feature'>
                    <span className='smart-condition-summary__ai-feature-label' id='smart-condition-summary-ai-feature-label'>
                        {translate('ABB.Powertrain.Frontend.tryAIFeatureForFreeLabel')}
                    </span>
                    <Tippy
                        content={
                            <div>
                                <div><strong>{translate('ABB.Powertrain.Frontend.aiFeaturesLabel')}</strong></div>
                                <div>{translate('ABB.Powertrain.Frontend.enjoyAIFeatureForFreeDescription')}</div>
                                <div>{translate('ABB.Powertrain.Frontend.futureAccessPremiumLicenseLabel')}</div>
                            </div>
                        }
                        interactive={true}
                        animation='scale'
                        theme='light-border'
                        trigger='click'
                        placement='top'
                    >
                        <button
                            type='button'
                            className='smart-condition-summary__ai-feature-info-btn'
                            id='smart-condition-summary-ai-feature-info-btn'
                            aria-label={translate('ABB.Powertrain.Frontend.aiFeaturesLabel')}
                        >
                            <IconInfo />
                        </button>
                    </Tippy>
                </div>
                <Switch
                    className='smart-condition-summary__switch'
                    id='smart-condition-summary-switch'
                    checked={isExpanded}
                    onChange={() => setIsExpanded(!isExpanded)}
                />
            </div>

            {/* Expanded Content - Diagnosis Cards */}
            {isExpanded && hasValidDiagnosisData &&
                <div className='smart-condition-summary__body' id='smart-condition-summary-body'>
                    {smartConditionSummaryData.map((diagnosis, index) => {
                        if (!diagnosis || !diagnosis?.failureType) {
                            return null;
                        }
                        return (
                            <DiagnosisCard
                                key={diagnosis?.failureType?.text || index}
                                diagnosis={diagnosis}
                                diagnosisNumber={index + 1}
                            />
                        );
                    })}
                </div>
            }

            {/* Footer Section - Feedback */}
            {isExpanded &&
                <div className='smart-condition-summary__footer' id='smart-condition-summary-footer'>
                    <div className='smart-condition-summary__disclaimer' id='smart-condition-summary-disclaimer'>
                        <span className='smart-condition-summary__disclaimer-text' id='smart-condition-summary-disclaimer-text'>
                            {translate('ABB.Powertrain.Frontend.aiDisclaimerDescription')}
                        </span>
                    </div>
                </div>
            }
        </div>
    );
};

SmartConditionSummary.propTypes = {
    smartConditionSummaryData: PropTypes.arrayOf(
        PropTypes.shape({
            failureType: PropTypes.object,
            faultDetectionLevel: PropTypes.object,
            diagnosis: PropTypes.object,
            causes: PropTypes.object,
            prediction: PropTypes.object,
            recommendationToConfirmDiagnosis: PropTypes.object,
            recommendationToFixProblem: PropTypes.object,
            isNeedsToShowRemainingUsefulLife: PropTypes.bool
        })
    )
};

export default SmartConditionSummary;
